import "@whitehaven/brand/tokens.css";
import "@whitehaven/ui/styles.css";
import type { SchedulingTimezone } from "@whitehaven/contracts";
import type { AuthorizationSubject } from "@whitehaven/permissions";
import { StatusPill } from "@whitehaven/ui";
import type { ReactNode } from "react";
import { focusMainContent } from "./focus-management";
import type { AdminConversationInboxGateways } from "./features/chat/admin-conversation-gateway";
import type { AdminLeadDetailGateway } from "./features/crm/lead-detail-gateway";
import type { AdminLeadDuplicateGateway } from "./features/crm/lead-duplicate-gateway";
import type { AdminLeadEngagementGateways } from "./features/crm/lead-engagement-gateway";
import type { AdminLeadGuestConversionMutationGateway } from "./features/crm/lead-guest-conversion-gateway";
import type { AdminLeadAppointmentConversionGateways } from "./features/crm/lead-appointment-conversion-gateway";
import type {
  AdminLeadAssigneeDirectoryGateway,
  AdminLeadAssignmentMutationGateway,
} from "./features/crm/lead-assignment-gateway";
import type { AdminLeadListGateway } from "./features/crm/lead-list-gateway";
import type { AdminLeadStageHistoryGateway } from "./features/crm/lead-stage-history-gateway";
import type {
  AdminLeadPipelineMutationGateway,
  AdminLeadPipelineReadGateway,
} from "./features/crm/lead-pipeline-gateway";
import type {
  AdminSchedulingCatalogMutationGateway,
  AdminSchedulingCatalogReadGateway,
  AdminSchedulingWorkspaceMutationGateway,
  AdminSchedulingWorkspaceReadGateway,
} from "./features/scheduling/scheduling-gateway";
import type { AdminConnectorSettingsGateways } from "./features/settings/admin-connector-settings-gateway";
import { PortalNavigation } from "./PortalNavigation";
import { PortalRouteOutlet } from "./PortalRouteOutlet";
import { PortalRouteErrorBoundary } from "./PortalRouteErrorBoundary";
import { resolvePortalRouteId } from "./route-error-model";
import type {
  StaffBarcodeControlGateway,
  StaffOperationsMutationGateway,
  StaffOperationsReadGateway,
  StaffPrintDialogAuditGateway,
  StaffVisitPacketGateway,
} from "./features/operations/staff-operations-gateway";
import {
  usePortalLocation,
  type PortalLocationState,
} from "./usePortalLocation";
import { useRouteFocus } from "./useRouteFocus";

interface AppProps {
  readonly subject?: AuthorizationSubject | null;
  readonly locationOverride?: PortalLocationState | null;
  readonly fallbackRoute?: ReactNode;
  readonly runtimeNotices?: ReactNode;
  readonly publicSiteHref?: string;
  readonly privacyHref?: string;
  readonly brandSubtitle?: string;
  readonly securityLabel?: string;
  readonly footerLabel?: string;
  readonly adminLeadListGateway?: AdminLeadListGateway | null;
  readonly adminLeadDetailGateway?: AdminLeadDetailGateway | null;
  readonly adminLeadDuplicateGateway?: AdminLeadDuplicateGateway | null;
  readonly adminLeadStageHistoryGateway?: AdminLeadStageHistoryGateway | null;
  readonly adminLeadAssigneeDirectoryGateway?: AdminLeadAssigneeDirectoryGateway | null;
  readonly adminLeadAssignmentMutationGateway?: AdminLeadAssignmentMutationGateway | null;
  readonly adminLeadEngagementGateways?: AdminLeadEngagementGateways | null;
  readonly adminLeadGuestConversionMutationGateway?: AdminLeadGuestConversionMutationGateway | null;
  readonly adminLeadAppointmentConversionGateways?: AdminLeadAppointmentConversionGateways | null;
  readonly adminLeadPipelineReadGateway?: AdminLeadPipelineReadGateway | null;
  readonly adminLeadPipelineMutationGateway?: AdminLeadPipelineMutationGateway | null;
  readonly adminConversationInboxGateways?: AdminConversationInboxGateways | null;
  readonly adminSchedulingCatalogReadGateway?: AdminSchedulingCatalogReadGateway | null;
  readonly adminSchedulingCatalogMutationGateway?: AdminSchedulingCatalogMutationGateway | null;
  readonly adminSchedulingWorkspaceReadGateway?: AdminSchedulingWorkspaceReadGateway | null;
  readonly adminSchedulingWorkspaceMutationGateway?: AdminSchedulingWorkspaceMutationGateway | null;
  readonly adminSchedulingAnchorDate?: string | undefined;
  readonly adminConnectorSettingsGateways?: AdminConnectorSettingsGateways | null;
  readonly staffOperationsReadGateway?: StaffOperationsReadGateway | null;
  readonly staffOperationsMutationGateway?: StaffOperationsMutationGateway | null;
  readonly staffVisitPacketGateway?: StaffVisitPacketGateway | null;
  readonly staffPrintDialogAuditGateway?: StaffPrintDialogAuditGateway | null;
  readonly staffBarcodeControlGateway?: StaffBarcodeControlGateway | null;
  readonly staffPrinterProfileId?: string | null;
  readonly staffOperationsSyntheticPreview?: boolean;
  readonly staffOperationsDate?: string | undefined;
  readonly staffOperationsLocationTimezones?: Readonly<
    Record<string, SchedulingTimezone | undefined>
  >;
  readonly homepageEditorRoute?: ReactNode;
  readonly roleAccessRoute?: ReactNode;
}

export default function App({
  subject = null,
  locationOverride = null,
  fallbackRoute,
  runtimeNotices = null,
  publicSiteHref = "/",
  privacyHref = "/privacy",
  brandSubtitle = "Secure portal",
  securityLabel = "Wellness-only · Secure access",
  footerLabel = "Whitehaven Wellness Hub · Secure portal foundation",
  adminLeadListGateway = null,
  adminLeadDetailGateway = null,
  adminLeadDuplicateGateway = null,
  adminLeadStageHistoryGateway = null,
  adminLeadAssigneeDirectoryGateway = null,
  adminLeadAssignmentMutationGateway = null,
  adminLeadEngagementGateways = null,
  adminLeadGuestConversionMutationGateway = null,
  adminLeadAppointmentConversionGateways = null,
  adminLeadPipelineReadGateway = null,
  adminLeadPipelineMutationGateway = null,
  adminConversationInboxGateways = null,
  adminSchedulingCatalogReadGateway = null,
  adminSchedulingCatalogMutationGateway = null,
  adminSchedulingWorkspaceReadGateway = null,
  adminSchedulingWorkspaceMutationGateway = null,
  adminSchedulingAnchorDate,
  adminConnectorSettingsGateways = null,
  staffOperationsReadGateway = null,
  staffOperationsMutationGateway = null,
  staffVisitPacketGateway = null,
  staffPrintDialogAuditGateway = null,
  staffBarcodeControlGateway = null,
  staffPrinterProfileId = null,
  staffOperationsSyntheticPreview = false,
  staffOperationsDate,
  staffOperationsLocationTimezones,
  homepageEditorRoute = null,
  roleAccessRoute = null,
}: AppProps) {
  const observedLocation = usePortalLocation();
  const location = locationOverride ?? observedLocation;
  useRouteFocus();

  const moveFocusToMainContent = () => {
    window.requestAnimationFrame(() => {
      focusMainContent();
    });
  };

  return (
    <div className="portal-shell">
      <a
        className="skip-link"
        href="#portal-title"
        onClick={moveFocusToMainContent}
      >
        Skip to portal content
      </a>
      {runtimeNotices}
      <aside className="portal-sidebar" aria-label="Secure portal navigation">
        <a
          href={publicSiteHref}
          className="portal-brand"
          aria-label="Whitehaven Wellness Hub public website"
        >
          <span className="portal-brand__mark" aria-hidden="true">
            <svg viewBox="0 0 64 64">
              <rect width="64" height="64" rx="18" fill="#fffcf5" />
              <circle cx="39" cy="24" r="12" fill="#f7c928" />
              <path
                d="M7 45c10-10 20-11 30-2 7 6 12 5 20-2v12H7z"
                fill="#0b6b45"
              />
              <path
                d="M13 38c8-11 15-17 24-20-4 7-6 13-7 20-6-5-11-5-17 0z"
                fill="#a9ddba"
              />
              <path
                d="M15 48c8-4 15-4 22 1 6 4 12 3 19-2"
                fill="none"
                stroke="#bde9e6"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className="portal-brand__copy">
            <b>Whitehaven</b>
            <small>Wellness Hub</small>
          </span>
        </a>

        <div className="portal-sidebar__status">
          <span className="portal-sidebar__status-icon" aria-hidden="true">
            <span className="portal-lock-icon" />
          </span>
          <span>
            <strong>Secure portal</strong>
            <small>{brandSubtitle}</small>
          </span>
        </div>

        <p className="portal-sidebar__label">Access</p>
        <PortalNavigation
          subject={subject}
          currentPath={location.pathname}
          publicSiteHref={publicSiteHref}
          privacyHref={privacyHref}
          homepageEditorAvailable={homepageEditorRoute !== null}
        />

        <div className="portal-sidebar__footer">
          <a href={publicSiteHref}>
            <span aria-hidden="true">↗</span>
            Visit public website
          </a>
        </div>
      </aside>

      <div className="portal-main-shell">
        <header className="portal-header">
          <div className="portal-breadcrumbs" aria-label="Current location">
            <span>Secure Portal</span>
            <span aria-hidden="true">›</span>
            <strong>Welcome</strong>
          </div>
          <StatusPill
            tone="positive"
            className="security-label"
            icon={<span className="security-label__dot" />}
            title="Private account access"
          >
            {securityLabel}
          </StatusPill>
        </header>

        <main id="portal-content" aria-labelledby="portal-title">
          <PortalRouteErrorBoundary
            routeId={resolvePortalRouteId(location.pathname)}
            navigationEpoch={location.navigationEpoch}
          >
            <PortalRouteOutlet
              pathname={location.pathname}
              subject={subject}
              adminLeadListGateway={adminLeadListGateway}
              adminLeadDetailGateway={adminLeadDetailGateway}
              adminLeadDuplicateGateway={adminLeadDuplicateGateway}
              adminLeadStageHistoryGateway={adminLeadStageHistoryGateway}
              adminLeadAssigneeDirectoryGateway={
                adminLeadAssigneeDirectoryGateway
              }
              adminLeadAssignmentMutationGateway={
                adminLeadAssignmentMutationGateway
              }
              adminLeadEngagementGateways={adminLeadEngagementGateways}
              adminLeadGuestConversionMutationGateway={
                adminLeadGuestConversionMutationGateway
              }
              adminLeadAppointmentConversionGateways={
                adminLeadAppointmentConversionGateways
              }
              adminLeadPipelineReadGateway={adminLeadPipelineReadGateway}
              adminLeadPipelineMutationGateway={
                adminLeadPipelineMutationGateway
              }
              adminConversationInboxGateways={adminConversationInboxGateways}
              adminSchedulingCatalogReadGateway={
                adminSchedulingCatalogReadGateway
              }
              adminSchedulingCatalogMutationGateway={
                adminSchedulingCatalogMutationGateway
              }
              adminSchedulingWorkspaceReadGateway={
                adminSchedulingWorkspaceReadGateway
              }
              adminSchedulingWorkspaceMutationGateway={
                adminSchedulingWorkspaceMutationGateway
              }
              adminSchedulingAnchorDate={adminSchedulingAnchorDate}
              adminConnectorSettingsGateways={adminConnectorSettingsGateways}
              staffOperationsReadGateway={staffOperationsReadGateway}
              staffOperationsMutationGateway={staffOperationsMutationGateway}
              staffVisitPacketGateway={staffVisitPacketGateway}
              staffPrintDialogAuditGateway={staffPrintDialogAuditGateway}
              staffBarcodeControlGateway={staffBarcodeControlGateway}
              staffPrinterProfileId={staffPrinterProfileId}
              staffOperationsSyntheticPreview={staffOperationsSyntheticPreview}
              staffOperationsDate={staffOperationsDate}
              staffOperationsLocationTimezones={
                staffOperationsLocationTimezones
              }
              homepageEditorRoute={homepageEditorRoute}
              roleAccessRoute={roleAccessRoute}
              fallbackRoute={fallbackRoute}
            />
          </PortalRouteErrorBoundary>
        </main>

        <footer className="portal-footer">
          <p>{footerLabel}</p>
          <p>Private portal · Wellness services only</p>
          <nav aria-label="Portal support">
            <a href={publicSiteHref}>Public website</a>
            <a href={privacyHref}>Privacy boundary</a>
            <a href="mailto:info@whitehavenwellness.com">Email support</a>
          </nav>
        </footer>
      </div>
    </div>
  );
}
